import React, { useState } from 'react';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { CreditCard, ShoppingCart } from 'phosphor-react';

// Import your payment components
import StripeCheckout from './StripeCheckout';
import StripePaymentElement from './StripePaymentElement';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

const PaymentOptionsDemo = () => {
  const [selectedOption, setSelectedOption] = useState(null);
  const [paymentComplete, setPaymentComplete] = useState(false);

  // Demo order data
  const demoOrderData = {
    order_number: `ORDER-${Date.now()}`,
    total_amount: 25.00,
    currency: 'EUR',
    user_id: 'demo-user',
    user_email: 'demo@example.com',
    items: [
      {
        product_id: '1',
        name: 'Handcrafted Card - Birthday',
        price: 25.00,
        quantity: 1
      }
    ],
    shipping_address: {
      first_name: 'Demo',
      last_name: 'User',
      address_line_1: '123 Demo Street',
      city: 'Brussels',
      postal_code: '1000',
      country: 'Belgium'
    }
  };

  const paymentOptions = [
    {
      id: 'current',
      title: 'Current: Card + QR Code',
      description: 'Your existing implementation with card payments and QR code payment links',
      icon: CreditCard,
      component: StripeCheckout,
      features: ['Card payments with Elements', 'QR code with Payment Links', 'Real-time polling']
    },
    {
      id: 'payment-element',
      title: 'Payment Element (Recommended)',
      description: 'Unified payment form supporting cards, wallets, bank transfers, and more',
      icon: ShoppingCart,
      component: StripePaymentElement,
      features: ['All payment methods in one element', 'Apple Pay, Google Pay', 'SEPA, iDEAL, Bancontact', 'Automatic payment method detection']
    }
  ];

  const handlePaymentSuccess = (paymentData) => {
    console.log('Payment successful:', paymentData);
    setPaymentComplete(true);
  };

  const handlePaymentError = (error) => {
    console.error('Payment error:', error);
    alert('Payment failed. Please try again.');
  };

  const handleCancel = () => {
    setSelectedOption(null);
    setPaymentComplete(false);
  };

  const resetDemo = () => {
    setSelectedOption(null);
    setPaymentComplete(false);
  };

  if (paymentComplete) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-xl shadow-lg p-8 text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h2 className="text-2xl font-semibold text-gray-800 mb-2">Payment Successful!</h2>
          <p className="text-gray-600 mb-6">Your order has been processed successfully.</p>
          <button
            onClick={resetDemo}
            className="w-full bg-gradient-to-r from-ambre-600 to-ambre-700 text-white py-3 rounded-lg hover:from-ambre-700 hover:to-ambre-800 transition-all duration-200"
          >
            Try Another Payment Method
          </button>
        </div>
      </div>
    );
  }

  if (selectedOption) {
    const option = paymentOptions.find(opt => opt.id === selectedOption);
    const PaymentComponent = option.component;

    return (
      <div className="min-h-screen bg-gray-50 p-4">
        <div className="max-w-4xl mx-auto">
          <div className="mb-6 text-center">
            <button
              onClick={handleCancel}
              className="text-gray-600 hover:text-gray-800 flex items-center mx-auto mb-4"
            >
              ← Back to Payment Options
            </button>
            <h1 className="text-3xl font-bold text-gray-800 mb-2">{option.title}</h1>
            <p className="text-gray-600">{option.description}</p>
          </div>

          <Elements stripe={stripePromise}>
            <PaymentComponent
              orderData={demoOrderData}
              onPaymentSuccess={handlePaymentSuccess}
              onPaymentError={handlePaymentError}
              onCancel={handleCancel}
            />
          </Elements>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-800 mb-4">Stripe Payment Integration Options</h1>
          <p className="text-xl text-gray-600">Choose a payment integration method to see it in action</p>
        </div>

        <div className="grid md:grid-cols-3 gap-8">
          {paymentOptions.map((option) => {
            const Icon = option.icon;
            return (
              <div key={option.id} className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow duration-300">
                <div className="p-6">
                  <div className="flex items-center mb-4">
                    <div className="w-12 h-12 bg-ambre-100 rounded-lg flex items-center justify-center mr-4">
                      <Icon className="w-6 h-6 text-ambre-600" />
                    </div>
                    <h3 className="text-xl font-semibold text-gray-800">{option.title}</h3>
                  </div>
                  
                  <p className="text-gray-600 mb-6">{option.description}</p>
                  
                  <div className="space-y-2 mb-6">
                    <h4 className="font-medium text-gray-800">Features:</h4>
                    <ul className="space-y-1">
                      {option.features.map((feature, index) => (
                        <li key={index} className="text-sm text-gray-600 flex items-center">
                          <div className="w-1.5 h-1.5 bg-ambre-600 rounded-full mr-2"></div>
                          {feature}
                        </li>
                      ))}
                    </ul>
                  </div>
                  
                  <button
                    onClick={() => setSelectedOption(option.id)}
                    className="w-full bg-gradient-to-r from-ambre-600 to-ambre-700 text-white py-3 rounded-lg hover:from-ambre-700 hover:to-ambre-800 transition-all duration-200 flex items-center justify-center space-x-2"
                  >
                    <span>Try This Method</span>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        <div className="mt-12 bg-white rounded-xl shadow-lg p-8">
          <h2 className="text-2xl font-semibold text-gray-800 mb-4">Demo Order Details</h2>
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <h3 className="font-medium text-gray-700 mb-2">Order Information</h3>
              <div className="space-y-1 text-sm text-gray-600">
                <p>Order: {demoOrderData.order_number}</p>
                <p>Total: €{demoOrderData.total_amount.toFixed(2)}</p>
                <p>Currency: {demoOrderData.currency}</p>
              </div>
            </div>
            <div>
              <h3 className="font-medium text-gray-700 mb-2">Items</h3>
              <div className="space-y-1 text-sm text-gray-600">
                {demoOrderData.items.map((item, index) => (
                  <p key={index}>{item.quantity}x {item.name} - €{item.price.toFixed(2)}</p>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentOptionsDemo; 